/**
 * Modo execução — a tela principal de quem opera o PCP (spec 4.3, design 02).
 *
 * Um passo por vez, lote sempre visível no topo, aviso logo abaixo do texto,
 * "Feito" grande no alcance do polegar. Feito avança direto (Voltar desfaz);
 * avisos aparecem antes do botão e nunca bloqueiam. Passo com referência vira
 * sub-checklist item a item. No desktop: execuções abertas | passo | índice.
 *
 * Cada toque vira evento na fila do aparelho e a tela mostra o efeito na hora
 * pela projeção local — com ou sem internet (ADR-0087).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiCheck,
  FiCheckCircle,
  FiChevronLeft,
  FiList,
  FiMoreHorizontal,
  FiPause,
  FiPlay,
  FiXCircle,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import styled, { css } from "styled-components";

import { EmptyStateErro } from "../../components/EmptyState";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import { ehFalhaDeRede, lerExecucao } from "../../services/areaPcp";
import { BASE, IndicadorConexao } from "./Casca";
import Folha from "./componentes/Folha";
import IniciarExecucao, { ROTA_EXECUCAO } from "./componentes/IniciarExecucao";
import MotivoFolha from "./componentes/MotivoFolha";
import PassoConteudo, { AvisoDoPasso } from "./componentes/PassoConteudo";
import { resumoLocal } from "./EmAndamento";
import {
  encerrada,
  estadoLocal,
  eventoDeCancelamento,
  eventoDePausa,
  eventoDeRetomada,
  eventosDeFeito,
  eventosDeIrPara,
  eventosDeVoltar,
  posicao,
  situacaoDoPasso,
} from "./dominio/execucao";
import { dataHoraLocal, duracao, haQuantoTempo, horaLocal, percentual } from "./dominio/formatos";
import { STATUS } from "./dominio/projecao";
import { useAreaPcp } from "./offline/AreaPcpContext";
import { PERM_EXECUTAR } from "./permissoes";
import { Aviso, BotaoPrimario, BotaoSecundario, BotaoTexto, Mono, Progresso, desktop } from "./styles";

const RETOMANDO_POR_MS = 4000;
const PARADA_PARA_RETOMANDO_MS = 10 * 60 * 1000;

// ------------------------------------------------------------------ layout

const Tela = styled.div`
  min-height: calc(100dvh - var(--app-header-height, 0px));
  background: var(--bg);
  display: grid;
  grid-template-columns: minmax(0, 1fr);

  @media ${desktop} {
    grid-template-columns: 260px minmax(0, 1fr) 320px;
  }
`;

const Coluna = styled.aside`
  display: none;

  @media ${desktop} {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    border-${({ $lado }) => ($lado === "esq" ? "right" : "left")}: 1px solid var(--border);
    background: var(--surface);
    max-height: calc(100dvh - var(--app-header-height, 0px));
    position: sticky;
    top: var(--app-header-height, 0px);
    overflow-y: auto;
  }

  > h2 {
    margin: 0 0 4px;
    font-size: 11px;
    letter-spacing: 0.06em;
    color: var(--ink-3);
  }
`;

const Centro = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Cabeca = styled.header`
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 50;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  .linha {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .ident {
    flex: 1 1 auto;
    min-width: 0;
  }

  .ident strong {
    display: block;
    font-family: var(--font-mono);
    font-size: clamp(15px, 4vw, 20px);
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ident span {
    display: block;
    font-size: 12px;
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .passo {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-2);
    font-variant-numeric: tabular-nums;
  }
`;

const Palco = styled.div`
  flex: 1 1 auto;
  padding: 18px 16px 160px;
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;

  @media ${desktop} {
    padding: 28px 32px 40px;
  }
`;

const Comandos = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 60;
  background: var(--surface);
  border-top: 1px solid var(--border);
  padding: 10px 12px calc(10px + var(--safe-area-bottom, 0px));
  display: flex;
  flex-direction: column;
  gap: 8px;

  .secundarios {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  @media ${desktop} {
    position: static;
    border: 0;
    background: transparent;
    padding: 0;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: flex-start;

    .secundarios {
      display: flex;
    }
  }
`;

/* O Feito é o único botão grande: é o toque que se repete o dia todo. */
const Feito = styled(BotaoPrimario)`
  min-height: 48px;
  font-size: 16px;
  border-radius: 10px;

  @media ${desktop} {
    min-height: 42px;
    min-width: 180px;
  }
`;

const ItemIndice = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 34px;
  padding: 4px 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  font: inherit;
  font-size: 13px;
  color: var(--ink-2);
  cursor: pointer;

  .n {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid var(--border-strong);
  }

  .t {
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .h {
    font-size: 11px;
    color: var(--ink-3);
    font-variant-numeric: tabular-nums;
  }

  ${({ $situacao }) =>
    $situacao === "feito" &&
    css`
      .n {
        background: var(--success-bg);
        border-color: var(--success-border);
        color: var(--success-text);
      }
    `}
  ${({ $situacao }) =>
    $situacao === "pulado" &&
    css`
      .t {
        text-decoration: line-through;
      }
    `}
  ${({ $situacao }) =>
    $situacao === "atual" &&
    css`
      background: var(--accent-soft);
      color: var(--ink);
      font-weight: 600;
      .n {
        background: var(--accent);
        border-color: var(--accent);
        color: var(--ink-on-accent);
      }
    `}
`;

const SubLista = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  overflow: hidden;

  li {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 9px 12px;
    font-size: 13.5px;
    color: var(--ink-2);
    border-top: 1px solid var(--border);
  }
  li:first-child {
    border-top: 0;
  }
  li[data-situacao="feito"] {
    color: var(--ink-3);
  }
  li[data-situacao="atual"] {
    background: var(--accent-soft);
    color: var(--ink);
    font-weight: 600;
  }
`;

const Conclusao = styled.div`
  max-width: 520px;
  margin: 40px auto;
  padding: 24px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;

  .selo {
    width: 72px;
    height: 72px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: var(--${({ $tom }) => $tom}-bg);
    color: var(--${({ $tom }) => $tom}-text);
    animation: crescer 320ms ease;
  }

  @keyframes crescer {
    from {
      transform: scale(0.8);
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .selo {
      animation: none;
    }
  }

  h1 {
    margin: 0;
    font-size: 22px;
  }

  dl {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    margin: 6px 0;
  }
  dt {
    font-size: 11px;
    letter-spacing: 0.06em;
    color: var(--ink-3);
  }
  dd {
    margin: 2px 0 0;
    font-size: 16px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .acoes {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
  }
`;

// ------------------------------------------------------------------ dados

function useExecucao(id) {
  const { request } = useAuth();
  const { pacote, atualizarExecucao } = useAreaPcp();
  const [remota, setRemota] = useState(null);
  const [erro, setErro] = useState(null);
  const local = (pacote?.execucoes || []).find((e) => e.id === id);

  useEffect(() => {
    let ativo = true;
    lerExecucao(request, id)
      .then((detalhe) => {
        if (!ativo) return;
        setRemota(detalhe);
        if (detalhe.status === STATUS.EM_ANDAMENTO || detalhe.status === STATUS.PAUSADA) atualizarExecucao(detalhe);
      })
      .catch((falha) => {
        if (ativo && !ehFalhaDeRede(falha)) setErro(falha);
      });
    return () => {
      ativo = false;
    };
  }, [request, id, atualizarExecucao]);

  // O pacote é a fonte viva (recebe as respostas da fila); a leitura direta
  // cobre a execução que não está nele (encerrada ou aberta noutro aparelho).
  const execucao = local || remota;
  return { execucao, erro, carregando: !execucao && !erro };
}

function vibrar(padrao) {
  try {
    navigator.vibrate?.(padrao);
  } catch {
    // aparelho sem vibração
  }
}

// ------------------------------------------------------------------ peças

function Indice({ roteiro, unidades, estado, onEscolher }) {
  return roteiro.passos.map((passo) => {
    const situacao = situacaoDoPasso(unidades, estado, passo);
    const marca = estado.unidades[unidades.filter((u) => u.passo === passo.chave).map((u) => u.chave).pop()];
    return (
      <ItemIndice key={passo.chave} type="button" $situacao={situacao} onClick={() => onEscolher(passo)} aria-current={situacao === "atual" ? "step" : undefined}>
        <span className="n">{situacao === "feito" ? <FiCheck size={12} aria-hidden="true" /> : passo.numero}</span>
        <span className="t">{passo.titulo_curto || passo.texto}</span>
        {passo.aviso ? <span aria-label="tem aviso" title="tem aviso">⚠</span> : null}
        {marca?.em ? <span className="h">{horaLocal(marca.em)}</span> : null}
      </ItemIndice>
    );
  });
}

function SubChecklist({ passo, estado }) {
  return (
    <SubLista aria-label="Sub-checklist">
      {passo.itens.map((item, i) => {
        let situacao = "a_fazer";
        if (estado.unidades[item.chave]) situacao = "feito";
        else if (estado.atual === item.chave) situacao = "atual";
        return (
          <li key={item.chave} data-situacao={situacao}>
            {situacao === "feito" ? <FiCheck size={14} aria-hidden="true" /> : <span style={{ width: 14 }}>{i + 1}.</span>}
            <span>{item.titulo_curto || item.texto}</span>
          </li>
        );
      })}
    </SubLista>
  );
}

function Encerramento({ execucao, estado, onPainel, onOutra }) {
  const concluida = estado.status === STATUS.CONCLUIDA;
  const fim = estado.concluida_em || estado.cancelada_em || execucao.concluida_em || execucao.cancelada_em;
  const segundos = fim ? (new Date(fim).getTime() - new Date(execucao.iniciada_em).getTime()) / 1000 : null;
  return (
    <Conclusao $tom={concluida ? "success" : "danger"} role="status">
      <div className="selo">{concluida ? <FiCheckCircle size={36} aria-hidden="true" /> : <FiXCircle size={36} aria-hidden="true" />}</div>
      <h1>
        {concluida ? `${execucao.tarefa.titulo} · concluída` : "Execução cancelada"}
      </h1>
      {execucao.identificador ? <Mono>{execucao.identificador}</Mono> : null}
      {!concluida && estado.motivo_cancelamento ? <p style={{ margin: 0 }}>Motivo: {estado.motivo_cancelamento}</p> : null}
      <dl>
        <div><dt>INÍCIO</dt><dd>{horaLocal(execucao.iniciada_em)}</dd></div>
        <div><dt>{concluida ? "FIM" : "CANCELADA"}</dt><dd>{horaLocal(fim)}</dd></div>
        <div><dt>DURAÇÃO</dt><dd>{duracao(segundos)}</dd></div>
      </dl>
      {concluida && execucao.roteiro.entrega_final ? (
        <Aviso $tom="success" style={{ textAlign: "left" }}>
          <div>
            <strong>Entrega</strong>
            <div>{execucao.roteiro.entrega_final}</div>
          </div>
        </Aviso>
      ) : null}
      <div className="acoes">
        <BotaoSecundario type="button" onClick={onPainel}>Voltar ao painel</BotaoSecundario>
        <BotaoPrimario type="button" onClick={onOutra}>Iniciar outra</BotaoPrimario>
      </div>
    </Conclusao>
  );
}

// ------------------------------------------------------------------ tela

export default function Execucao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { pacote, pendentes, registrar } = useAreaPcp();
  const { execucao, erro, carregando } = useExecucao(id);
  const ehDesktop = useMediaQuery(desktop);
  const podeExecutar = hasPermission(PERM_EXECUTAR);

  const [indiceAberto, setIndiceAberto] = useState(false);
  const [lotesAbertos, setLotesAbertos] = useState(false);
  const [mais, setMais] = useState(false);
  const [motivo, setMotivo] = useState(null);
  const [saltoPara, setSaltoPara] = useState(null);
  const [outra, setOutra] = useState(false);
  const [retomando, setRetomando] = useState(false);

  const calculado = useMemo(() => {
    if (!execucao) return null;
    const { unidades, estado } = estadoLocal(execucao, pendentes[id]);
    return { unidades, estado, onde: posicao(execucao.roteiro, unidades, estado) };
  }, [execucao, pendentes, id]);

  useEffect(() => {
    if (!execucao) return undefined;
    const parada = Date.now() - new Date(execucao.ultima_atividade_em).getTime() > PARADA_PARA_RETOMANDO_MS;
    if (!parada) return undefined;
    setRetomando(true);
    const timer = setTimeout(() => setRetomando(false), RETOMANDO_POR_MS);
    return () => clearTimeout(timer);
    // Só ao abrir esta execução: a faixa é de "voltei para cá", não de cada toque.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execucao?.id]);

  const tocar = useCallback(
    (eventos) => {
      setRetomando(false);
      registrar(id, eventos);
    },
    [registrar, id],
  );

  const feito = useCallback(() => {
    if (!calculado) return;
    const eventos = eventosDeFeito(calculado.estado);
    const ultimo = calculado.estado.concluidos + 1 === calculado.estado.total;
    vibrar(ultimo ? [15, 60, 15] : 15);
    tocar(eventos);
  }, [calculado, tocar]);

  const voltar = useCallback(() => {
    if (!calculado) return;
    const eventos = eventosDeVoltar(calculado.unidades, calculado.estado);
    if (!eventos.length) return;
    tocar(eventos);
    toast.info("Passo reaberto");
  }, [calculado, tocar, toast]);

  const aberta = calculado && !encerrada(calculado.estado);

  useEffect(() => {
    if (!aberta || !podeExecutar) return undefined;
    const aoTeclar = (e) => {
      if (motivo || saltoPara || mais || indiceAberto || lotesAbertos) return;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName)) return;
      if (e.key === "Enter") {
        e.preventDefault();
        feito();
      } else if (e.key === "ArrowLeft" || e.key === "Backspace") {
        e.preventDefault();
        voltar();
      } else if (e.key.toLowerCase() === "p") {
        setMotivo("pausa");
      } else if (e.key.toLowerCase() === "i") {
        setIndiceAberto(true);
      } else if (e.key.toLowerCase() === "c") {
        const trecho = (calculado.onde.item || calculado.onde.passo)?.trecho_copiavel;
        if (trecho) navigator.clipboard?.writeText(trecho).then(() => toast.success("Copiado"), () => {});
      }
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [aberta, podeExecutar, feito, voltar, motivo, saltoPara, mais, indiceAberto, lotesAbertos, calculado, toast]);

  if (carregando) return <PageLoader scope="kpis" />;
  if (erro) return <EmptyStateErro erro={erro} titulo="Não conseguimos abrir esta execução" />;
  if (!execucao) {
    return <EmptyStateErro erro={{ message: "Esta execução não está guardada neste aparelho. Conecte-se para abrir." }} />;
  }

  const { roteiro } = execucao;
  const { unidades, estado, onde } = calculado;
  const painel = () => navigate(`${BASE}/em-andamento`);

  if (!aberta) {
    // "Iniciar outra" usa a tarefa como está agora (rótulo e regra do
    // identificador), não a do roteiro congelado.
    const tarefaAtual = (pacote?.tarefas || []).find((t) => t.id === execucao.tarefa.id);
    return (
      <>
        <Encerramento
          execucao={execucao}
          estado={estado}
          onPainel={painel}
          onOutra={() => (tarefaAtual ? setOutra(true) : navigate(`${BASE}/tarefas`))}
        />
        {tarefaAtual ? (
          <IniciarExecucao
            tarefa={{ ...tarefaAtual, n_passos: tarefaAtual.passos.length }}
            aberta={outra}
            onFechar={() => setOutra(false)}
          />
        ) : null}
      </>
    );
  }

  const motivos = pacote?.motivos || { pausa: [], cancelamento: [] };
  const outrasAbertas = (pacote?.execucoes || [])
    .filter((e) => e.id !== id)
    .map((e) => resumoLocal(e, pendentes[e.id]))
    .filter((e) => e.status === STATUS.EM_ANDAMENTO || e.status === STATUS.PAUSADA);
  const pct = percentual(estado.concluidos, estado.total);
  const passoTexto = `passo ${onde.numero} de ${onde.total}`;
  const passoNaTela = onde.passo ? { ...onde.passo } : null;
  const indice = (
    <Indice
      roteiro={roteiro}
      unidades={unidades}
      estado={estado}
      onEscolher={(passo) => {
        setIndiceAberto(false);
        if (passo.chave !== onde.passo?.chave && podeExecutar) setSaltoPara(passo);
      }}
    />
  );
  const listaDeLotes = outrasAbertas.length ? outrasAbertas.map((e) => (
    <ItemIndice key={e.id} type="button" onClick={() => { setLotesAbertos(false); navigate(ROTA_EXECUCAO(e.id)); }}>
      <span className="t">
        <Mono>{e.identificador || e.tarefa.titulo}</Mono>
        <br />
        <small>
          Passo {e.numero} · {e.status === STATUS.PAUSADA ? `pausado ${haQuantoTempo(e.ultima_atividade_em)}` : e.titulo_passo}
        </small>
      </span>
    </ItemIndice>
  )) : <small style={{ color: "var(--ink-3)" }}>Nenhuma outra execução aberta.</small>;

  return (
    <Tela>
      <Coluna $lado="esq" aria-label="Execuções abertas">
        <h2>EXECUÇÕES ABERTAS</h2>
        {listaDeLotes}
        <BotaoTexto type="button" onClick={painel} style={{ justifyContent: "flex-start" }}>
          <FiArrowLeft size={14} aria-hidden="true" /> Painel
        </BotaoTexto>
      </Coluna>

      <Centro>
        <Cabeca>
          <div className="linha">
            <BotaoSecundario type="button" aria-label="Voltar ao painel" onClick={painel} style={{ width: 34, padding: 0 }}>
              <FiChevronLeft size={18} aria-hidden="true" />
            </BotaoSecundario>
            <div className="ident">
              <strong title={execucao.identificador}>{execucao.identificador || execucao.tarefa.titulo}</strong>
              <span>{execucao.identificador ? execucao.tarefa.titulo : `versão ${roteiro.versao}`}</span>
            </div>
            <IndicadorConexao compacto={!ehDesktop} />
            {!ehDesktop ? (
              <BotaoSecundario type="button" aria-label="Trocar de lote" onClick={() => setLotesAbertos(true)} style={{ minWidth: 44, padding: "0 10px" }}>
                {outrasAbertas.length + 1}
              </BotaoSecundario>
            ) : null}
          </div>
          <div className="passo">
            <span>Passo {onde.numero} de {onde.total}</span>
            {!ehDesktop ? (
              <BotaoTexto type="button" onClick={() => setIndiceAberto(true)} style={{ minHeight: 28, padding: 0 }}>
                <FiList size={14} aria-hidden="true" /> Ver índice
              </BotaoTexto>
            ) : <span>{pct}%</span>}
          </div>
          <Progresso $pct={pct} $altura={8} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <span />
          </Progresso>
        </Cabeca>

        <Palco>
          {retomando ? (
            <Aviso $tom="info" role="status">Retomando de onde você parou ({dataHoraLocal(execucao.ultima_atividade_em)}).</Aviso>
          ) : null}
          {estado.status === STATUS.PAUSADA ? (
            <Aviso $tom="neutral">
              <div style={{ flex: 1 }}>
                <strong>PAUSADO</strong> {estado.motivo_pausa ? `· ${estado.motivo_pausa}` : ""}
                <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  {execucao.ultima_atividade_por || "—"} · {haQuantoTempo(execucao.ultima_atividade_em)}
                </div>
              </div>
              {podeExecutar ? (
                <BotaoSecundario type="button" onClick={() => tocar(eventoDeRetomada())}>
                  <FiPlay size={14} aria-hidden="true" /> Retomar
                </BotaoSecundario>
              ) : null}
            </Aviso>
          ) : null}

          <PassoConteudo passo={passoNaTela} grande>
            {passoNaTela?.referencia ? (
              <Aviso $tom="info">
                {/* Um só filho: o Aviso é flex e partiria a frase em colunas. */}
                <span>
                  Passos {passoNaTela.referencia.de} a {passoNaTela.referencia.ate} de{" "}
                  <strong>{passoNaTela.referencia.tarefa_titulo}</strong>, aqui embaixo · {onde.feitosNoPasso} /{" "}
                  {passoNaTela.itens.length}
                </span>
              </Aviso>
            ) : null}
          </PassoConteudo>
          {onde.item ? (
            <>
              <SubChecklist passo={onde.passo} estado={estado} />
              <div style={{ padding: 14, border: "1px solid var(--accent)", borderRadius: 12, background: "var(--surface)" }}>
                <PassoConteudo passo={{ ...onde.item, numero: null, sistema: `${onde.item.origem}${onde.item.sistema ? ` · ${onde.item.sistema}` : ""}` }} grande />
              </div>
            </>
          ) : null}

          {podeExecutar ? (
            <Comandos>
              <Feito type="button" onClick={feito} aria-keyshortcuts="Enter">
                <FiCheck size={20} aria-hidden="true" />
                {onde.item ? `Feito · item ${onde.itemIndice + 1}` : "Feito"}
              </Feito>
              <div className="secundarios">
                <BotaoSecundario type="button" onClick={voltar} disabled={!estado.concluidos} aria-keyshortcuts="ArrowLeft">
                  <FiArrowLeft size={15} aria-hidden="true" /> Voltar
                </BotaoSecundario>
                <BotaoSecundario type="button" onClick={() => setMotivo("pausa")} aria-keyshortcuts="P">
                  <FiPause size={15} aria-hidden="true" /> Pausar
                </BotaoSecundario>
                <BotaoSecundario type="button" onClick={() => setMais(true)}>
                  <FiMoreHorizontal size={15} aria-hidden="true" /> Mais
                </BotaoSecundario>
              </div>
            </Comandos>
          ) : (
            <Aviso $tom="neutral">Você pode acompanhar esta execução, mas não marcar passos (falta a permissão de executar).</Aviso>
          )}
        </Palco>
      </Centro>

      <Coluna $lado="dir" aria-label="Índice de passos">
        <h2>ÍNDICE · {onde.passosConcluidos} feitos · {onde.total - onde.passosConcluidos} restantes</h2>
        {indice}
        {onde.proximo ? (
          <div style={{ marginTop: 12, padding: 12, border: "1px solid var(--border)", borderRadius: 12 }}>
            <PassoConteudo passo={{ ...onde.proximo, aviso: "" }} />
            {onde.proximo.aviso ? <AvisoDoPasso texto={onde.proximo.aviso} /> : null}
          </div>
        ) : null}
      </Coluna>

      <Folha aberta={indiceAberto} onFechar={() => setIndiceAberto(false)} titulo="Índice" subtitulo={`${onde.passosConcluidos} feitos · ${onde.total - onde.passosConcluidos} restantes`}>
        {indice}
      </Folha>
      <Folha aberta={lotesAbertos} onFechar={() => setLotesAbertos(false)} titulo="Execuções abertas">
        {listaDeLotes}
      </Folha>
      <Folha
        aberta={mais}
        onFechar={() => setMais(false)}
        titulo="Mais ações"
        acoes={<BotaoSecundario type="button" onClick={() => setMais(false)}>Fechar</BotaoSecundario>}
      >
        <BotaoSecundario type="button" onClick={() => { setMais(false); setIndiceAberto(true); }}>
          <FiList size={15} aria-hidden="true" /> Índice e ir para outro passo
        </BotaoSecundario>
        <BotaoSecundario type="button" onClick={() => { setMais(false); setMotivo("cancelamento"); }} style={{ color: "var(--danger-text)" }}>
          <FiXCircle size={15} aria-hidden="true" /> Cancelar execução…
        </BotaoSecundario>
      </Folha>
      <Folha
        aberta={Boolean(saltoPara)}
        onFechar={() => setSaltoPara(null)}
        titulo={saltoPara ? `Ir para o passo ${saltoPara.numero}?` : ""}
        subtitulo={
          saltoPara && saltoPara.numero > onde.numero
            ? "Os passos entre o atual e este ficam registrados como pulados."
            : "Os passos a partir deste voltam a “a fazer”."
        }
        acoes={
          <>
            <BotaoSecundario type="button" onClick={() => setSaltoPara(null)}>Ficar aqui</BotaoSecundario>
            <BotaoPrimario
              type="button"
              onClick={() => {
                tocar(eventosDeIrPara(unidades, estado, saltoPara.chave));
                setSaltoPara(null);
              }}
            >
              Ir para este passo
            </BotaoPrimario>
          </>
        }
      >
        {saltoPara ? <PassoConteudo passo={saltoPara} /> : null}
      </Folha>
      <MotivoFolha
        tipo={motivo}
        aberta={Boolean(motivo)}
        onFechar={() => setMotivo(null)}
        motivos={motivo === "cancelamento" ? motivos.cancelamento : motivos.pausa}
        identificador={execucao.identificador}
        passoTexto={passoTexto}
        concluidos={onde.passosConcluidos}
        onConfirmar={(texto) => {
          tocar(motivo === "cancelamento" ? eventoDeCancelamento(texto) : eventoDePausa(texto));
          if (motivo === "pausa") toast.info("Lote pausado. Continuar abre direto neste passo.");
          setMotivo(null);
        }}
      />
    </Tela>
  );
}
